import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { DropzoneOptions, useDropzone } from 'react-dropzone'
import { useNavigate } from 'react-router-dom'
import ErrorDialog, { formatError } from '../../components/dialogs/ErrorDialog'
import { EdiToJson } from '../../services/miscService'
import { submitEligibility, verifyEligibility } from '../../services/patientService'

export const SubmitEligibility: React.FC = () => {
    const navigate = useNavigate()
    const [importType, setImportType] = useState<'file' | 'clipboard' | 'mock'>('file')
    const [contentType, setContentType] = useState<'json' | 'edi' | 'unknown'>('unknown')
    const [ediContent, setEdiContent] = useState('')
    const [errorData, setErrorData] = useState<any>()
    const [errorOpen, setErrorOpen] = useState(false)
    const [validationPassed, setValidationPassed] = useState(false)
    const [loading, setLoading] = useState(false)

    const onDrop = async ([file]: any) => {
        const reader = new FileReader()
        reader.onload = function (e: any) {
            const contents = e.target.result
            setContentType(checkJson(contents) ? 'json' : 'edi')
            setEdiContent(contents)
        }
        reader.readAsText(file)
    }
    const dropzoneOptions: DropzoneOptions = { onDrop }
    const { acceptedFiles, getRootProps, getInputProps } = useDropzone(dropzoneOptions)
    useEffect(() => {
        if (acceptedFiles.length === 0) {
            setValidationPassed(false)
        }
    }, [acceptedFiles])
    const checkJson = (value: any) => {
        try {
            return JSON.parse(value)
        } catch (e) {
            return undefined
        }
    }
    const handleProcessEDI = async (submit: boolean, data: FormData) => {
        try {
            const request =
                contentType === 'json' ? JSON.parse(ediContent) : ({ x12: ediContent } as any)
            if (contentType === 'edi') {
                const {
                    data: { headers },
                } = await EdiToJson(data)
                request.headers = headers
            }
            await verifyEligibility(request)
                .then(async (response) => {
                    if (response.data) {
                        setValidationPassed(true)
                        if (contentType === 'edi') {
                            request.payload = response.data?.payload
                        }
                        if (submit) {
                            await submitEligibility(request)
                            navigate('/eligibility/transactions')
                        }
                    }
                    if (response.error) {
                        setErrorModal(response?.error)
                    }
                })
                .catch((e) => {
                    setErrorModal(e)
                })
                .finally(() => setLoading(false))
        } catch (e) {
            setErrorModal(e)
        } finally {
            setLoading(false)
        }
        return
    }

    const setErrorModal = (error: any) => {
        setErrorData(formatError(error))
        setErrorOpen(true)
    }
    const processEDI = async (submit: boolean) => {
        setLoading(true)
        setErrorOpen(false)
        const data = new FormData()
        switch (importType) {
            case 'file':
                data.append('files', acceptedFiles[0])
                break
            case 'clipboard':
                data.append('files', new Blob([ediContent]))
                break
        }
        await handleProcessEDI(submit, data)
    }
    const fileTab = (
        <Box
            {...getRootProps()}
            style={{
                borderRadius: '10px',
                height: '192px',
                border: 'dashed 1px #008AEE',
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
            }}
        >
            <input {...getInputProps()} />
            {acceptedFiles.length === 0 ? (
                <Typography style={{ lineHeight: '200px' }}>
                    Click here or drag file to this area
                </Typography>
            ) : (
                <Typography
                    style={{ lineHeight: '200px' }}
                >{`File ${acceptedFiles[0].name} selected`}</Typography>
            )}
        </Box>
    )
    const ediContentTab = (
        <Box>
            <TextField
                value={ediContent}
                onChange={(event) => {
                    setValidationPassed(false)
                    setEdiContent(event.target.value)
                    setContentType(checkJson(event.target.value) ? 'json' : 'edi')
                }}
                style={{ backgroundColor: 'white' }}
                multiline
                fullWidth
                rows={7}
            />
        </Box>
    )
    return (
        <Grid container direction="column" style={{ padding: '40px' }}>
            <Grid item>
                <Typography variant="h3">Submit an eligibility request</Typography>
            </Grid>
            <Grid item>
                <Typography style={{ marginTop: '10px' }}>
                    Use the form below to submit new eligibility request to the Rhiza Health system.
                </Typography>
            </Grid>
            <Grid item>
                <Box
                    style={{
                        borderRadius: '10px',
                        marginTop: '30px',
                        padding: '30px',
                        backgroundColor: '#F2F6F9',
                    }}
                >
                    <Typography variant="h6">Upload Eligibility Request</Typography>
                    <Typography style={{ marginTop: '10px' }}>
                        Eligibility Request should be added using X12 data format.
                    </Typography>
                    <ToggleButtonGroup
                        style={{ marginTop: '30px' }}
                        size="small"
                        color="primary"
                        value={importType}
                        exclusive
                        onChange={(_event, value) => {
                            setImportType(value ?? importType)
                        }}
                    >
                        <ToggleButton value="file">Upload data file</ToggleButton>
                        <ToggleButton value="clipboard">Paste from clipboard</ToggleButton>
                    </ToggleButtonGroup>
                    {
                        <Box sx={{ marginTop: '30px' }}>
                            <ToggleButtonGroup
                                style={{
                                    marginTop: '30px',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                                size="small"
                                color="primary"
                                value={contentType}
                                disabled
                                exclusive
                                onChange={(_event, value) => {
                                    setContentType(value ?? contentType)
                                }}
                            >
                                <ToggleButton value="json" sx={{ height: '30px', width: '8%' }}>
                                    JSON
                                </ToggleButton>
                                <ToggleButton value="edi" sx={{ height: '30px', width: '8%' }}>
                                    EDI
                                </ToggleButton>
                            </ToggleButtonGroup>
                            {importType === 'file' ? fileTab : ediContentTab}
                        </Box>
                    }
                    <Box
                        style={{
                            marginTop: '30px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box>
                            {validationPassed && (
                                <Typography style={{ color: 'green' }}>
                                    Successfully validated eligibility request targeted to
                                    recipient.
                                </Typography>
                            )}
                        </Box>
                        <Box style={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                size="large"
                                disableElevation={true}
                                variant="contained"
                                disabled={
                                    (importType === 'file' && acceptedFiles.length === 0) ||
                                    (importType === 'clipboard' && ediContent.length === 0) ||
                                    validationPassed
                                }
                                onClick={() => processEDI(false)}
                                style={{ minWidth: '120px' }}
                            >
                                Validate
                            </Button>
                            <Button
                                size="large"
                                disableElevation={true}
                                variant="contained"
                                onClick={() => processEDI(true)}
                                style={{ marginLeft: '10px', minWidth: '120px' }}
                                disabled={!validationPassed}
                            >
                                Submit
                            </Button>
                        </Box>
                    </Box>
                    <Backdrop open={loading}>
                        {' '}
                        <CircularProgress color="inherit" />
                    </Backdrop>
                </Box>
            </Grid>
            <ErrorDialog
                open={errorOpen}
                data={errorData}
                onClose={() => {
                    setLoading(false)
                    setErrorOpen(false)
                }}
            />
        </Grid>
    )
}
